import { Resolver } from '@lucidlink/tscore';
const IMMEDIATE = 0;
export const MAX_BATCH_SIZE = 100;
/**
 * Coalesces per-entry path lookups issued together into a single
 * `getPathsByIdsBulk` call (data loader pattern), so N per-row queries cost
 * one hub round-trip while each entry keeps its own React Query cache entry.
 */
class FsEntryPathLoader {
    pendingByEntryId = new Map();
    flushTimer;
    fetchPaths;
    load({ filespaceId, fsEntryId, plugFilespaceClient, workspaceId, }) {
        // Latest caller wins, so a flush never uses a stale SDK closure
        this.fetchPaths = (ids) => plugFilespaceClient({
            action: (fsClient) => fsClient.fileSystem().getPathsByIdsBulk(ids),
            filespaceId,
            workspaceId,
        });
        let resolver = this.pendingByEntryId.get(fsEntryId);
        if (!resolver) {
            resolver = new Resolver();
            this.pendingByEntryId.set(fsEntryId, resolver);
        }
        // Each new query re-arms the flush, so a synchronous burst of loads
        // flushes as one batch on the next macrotask
        clearTimeout(this.flushTimer);
        if (this.pendingByEntryId.size >= MAX_BATCH_SIZE) {
            this.flushTimer = undefined;
            void this.flush();
        }
        else {
            this.flushTimer = setTimeout(() => void this.flush(), IMMEDIATE);
        }
        return resolver.promise;
    }
    async flush() {
        const batch = this.pendingByEntryId;
        this.pendingByEntryId = new Map();
        this.flushTimer = undefined;
        try {
            const results = await this.fetchPaths(Array.from(batch.keys()));
            const resultsByEntryId = new Map(results.map((result) => [result.id, result]));
            for (const [fsEntryId, resolver] of batch) {
                resolver.resolve(toPathResult(resultsByEntryId.get(fsEntryId)));
            }
        }
        catch (error) {
            const bulkError = error instanceof Error ? error : new Error(String(error));
            for (const resolver of batch.values()) {
                resolver.reject(bulkError);
            }
        }
    }
}
const loaders = new Map();
export const loadFsEntryPath = (args) => {
    let loader = loaders.get(args.filespaceId);
    if (!loader) {
        loader = new FsEntryPathLoader();
        loaders.set(args.filespaceId, loader);
    }
    return loader.load(args);
};
const toPathResult = (result) => {
    if (result?.path !== undefined) {
        return {
            status: 'resolved',
            path: result.path,
            ...(result.type && { type: result.type }),
        };
    }
    if (result?.errorCode === 'invalid-file-id') {
        return { status: 'deleted' };
    }
    return { status: 'failed' };
};
